import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  TRANSITIONS_V2_DATA,
  TransitionsEntityKind,
} from '@revelio/data-access';

import { applyGeoFilter } from '../utils/applyGeoFilter';
import type { AdjacentRole } from './useAdjacentRoles';
import type {
  GeoLevel,
  RoleGranularity,
  RoleLevel,
} from './useResolveProfileIds';

/** A role that feeds into an adjacent role */
export interface InflowRole {
  id: number;
  shortName: string;
  longName: string;
  /** Share of inflows from this role (0-1) */
  inflowShare: number;
  inflowCount: number;
  /** Whether this is the user's current role */
  isCurrentRole: boolean;
}

/** Inflow data for a single adjacent role */
export interface RoleInflowData {
  roleId: number;
  inflows: InflowRole[];
  /** The user's current role share as an inflow (null if not a feeder) */
  currentRoleInflowShare: number | null;
}

interface UseRoleInflowsResult {
  inflowsByRole: Map<number, RoleInflowData>;
  isLoading: boolean;
  progress: number;
}

const MAX_ROLES_TO_QUERY = 5;
const TOP_INFLOWS = 7;

const ROLE_GRANULARITY_TO_ENTITY: Record<
  RoleGranularity,
  TransitionsEntityKind
> = {
  role_k15000: TransitionsEntityKind.RoleK15000,
  role_k1500: TransitionsEntityKind.RoleK1500,
  role_k150: TransitionsEntityKind.RoleK150,
  role_k7: TransitionsEntityKind.RoleK7,
};

export const useRoleInflows = (
  adjacentRoles: AdjacentRole[],
  currentRoleLevel: RoleLevel | null,
  selectedGeo: GeoLevel | null,
  roleGranularity: RoleGranularity = 'role_k150'
): UseRoleInflowsResult => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<Map<number, RoleInflowData>>(
    new Map()
  );
  const prevKeyRef = useRef<string>('');

  const rolesToQuery = adjacentRoles.slice(0, MAX_ROLES_TO_QUERY);

  // Reset when adjacent roles change
  const rolesKey = rolesToQuery.map((r) => r.id).join(',');
  useEffect(() => {
    if (rolesKey !== prevKeyRef.current) {
      prevKeyRef.current = rolesKey;
      setCurrentIndex(0);
      setResults(new Map());
    }
  }, [rolesKey]);

  const currentRole =
    currentIndex < rolesToQuery.length ? rolesToQuery[currentIndex] : undefined;

  const entityType =
    ROLE_GRANULARITY_TO_ENTITY[roleGranularity] ??
    TransitionsEntityKind.RoleK150;

  const variables = useMemo(() => {
    if (!currentRole || currentIndex >= rolesToQuery.length) {
      return undefined;
    }

    const filters: Record<string, unknown> = {};
    if (selectedGeo) {
      applyGeoFilter(filters, selectedGeo);
    }

    return {
      req: {
        entities: [
          {
            EntID: currentRole.id,
            EntType: entityType,
          },
        ],
        filters,
      },
    };
  }, [currentRole, currentIndex, rolesToQuery.length, selectedGeo, entityType]);

  const [{ data, fetching, error }] = useQuery({
    query: TRANSITIONS_V2_DATA,
    variables: variables ?? {
      req: { entities: [], filters: {} },
    },
    pause: !variables,
  });

  const currentRoleId = currentRoleLevel?.id ?? null;

  // Process inflows when data arrives and advance
  useEffect(() => {
    if (!currentRole || fetching || currentIndex >= rolesToQuery.length) {
      return;
    }

    if (data || error) {
      const inflowData = processInflows(currentRole.id, data, currentRoleId);

      setResults((prev) => {
        const next = new Map(prev);
        next.set(currentRole.id, inflowData);
        return next;
      });
      setCurrentIndex((prev) => prev + 1);
    }
  }, [
    data,
    error,
    fetching,
    currentRole,
    currentIndex,
    rolesToQuery.length,
    currentRoleId,
  ]);

  const isLoading =
    rolesToQuery.length > 0 && currentIndex < rolesToQuery.length;

  const progress =
    rolesToQuery.length > 0 ? currentIndex / rolesToQuery.length : 1;

  return {
    inflowsByRole: results,
    isLoading,
    progress,
  };
};

function processInflows(
  targetRoleId: number,
  data: unknown,
  currentRoleId: number | null
): RoleInflowData {
  const empty: RoleInflowData = {
    roleId: targetRoleId,
    inflows: [],
    currentRoleInflowShare: null,
  };

  const transitionsData = data as {
    transitionsV2?: {
      entities?: Array<{
        metrics?: {
          flows?: {
            inflows?: Array<{
              id?: number | null;
              short_name?: string | null;
              long_name?: string | null;
              count?: number | null;
              share?: number | null;
            } | null> | null;
          } | null;
        } | null;
      } | null> | null;
    } | null;
  } | null;

  const entity = transitionsData?.transitionsV2?.entities?.[0];
  const rawInflows = entity?.metrics?.flows?.inflows;
  if (!rawInflows) {
    return empty;
  }

  let currentRoleInflowShare: number | null = null;

  const inflows: InflowRole[] = rawInflows
    .filter(
      (flow): flow is NonNullable<typeof flow> =>
        flow !== null && flow !== undefined
    )
    .filter((flow) => {
      const name = (flow.long_name || flow.short_name || '').toLowerCase();
      return name !== 'unknown' && name !== 'empty' && name !== '';
    })
    .map((flow) => {
      const isCurrentRole = currentRoleId !== null && flow.id === currentRoleId;
      // API returns share as percentage (e.g. 6.07 = 6.07%), normalize to 0-1
      const inflowShare = (flow.share ?? 0) / 100;

      if (isCurrentRole) {
        currentRoleInflowShare = inflowShare;
      }

      return {
        id: flow.id ?? 0,
        shortName: flow.short_name ?? '',
        longName: flow.long_name ?? '',
        inflowShare,
        inflowCount: flow.count ?? 0,
        isCurrentRole,
      };
    })
    .sort((a, b) => b.inflowShare - a.inflowShare)
    .slice(0, TOP_INFLOWS);

  return {
    roleId: targetRoleId,
    inflows,
    currentRoleInflowShare,
  };
}
